import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { succeeded } from './asyncResult';
import { ComingSoonTag } from './ComingSoonTag';
import { CredentialForm } from './CredentialForm';
import { shouldShowCredentialForm, shouldShowNotEntitledNotice } from './entitlementGate';
import { credentialFailureMessage, credentialStatusLabel, credentialStatusVariant } from './credentialStatus';
export { credentialStatusLabel, credentialStatusVariant, credentialFailureMessage } from './credentialStatus';
export { findCredentialForUtility } from './credentialStatus';
export { CredentialForm } from './CredentialForm';
const formatDate = value => value ? new Date(value).toLocaleDateString() : null;

/**
 * Credential state + actions for a single property utility.
 *
 * Staff-only. The caller decides whether to render it (store.account.userProfile.isUserLivableStaff),
 * but that is a convenience, not the control: every endpoint behind this is Permissions.AdminOrHigher,
 * so a customer who forced the component to render would still get 403s.
 */
export const UtilityPortalCredentialSection = observer(_ref => {
  let companyId = _ref.companyId,
    utilityProviderId = _ref.utilityProviderId,
    propertyId = _ref.propertyId,
    propertyUtilityId = _ref.propertyUtilityId,
    utilityTypeId = _ref.utilityTypeId,
    propertyReference = _ref.propertyReference,
    credential = _ref.credential,
    onChanged = _ref.onChanged;
  const store = useStore();
  const _useState = useState(!credential),
    _useState2 = _slicedToArray(_useState, 2),
    isEditing = _useState2[0],
    setIsEditing = _useState2[1];
  // Seeded from a prop that can arrive after mount. saas-react-components v3 keeps modal children
  // mounted while the modal is open, so the remount that used to re-seed this no longer happens
  // and a credential loading late would leave the section stuck on the form. Keyed on the id, not
  // the object, so a refetch returning the same credential does not interrupt an edit in progress.
  const credentialId = credential === null || credential === void 0 ? void 0 : credential.id;
  useEffect(() => setIsEditing(!credentialId), [credentialId]);
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    busy = _useState4[0],
    setBusy = _useState4[1];
  const _useState5 = useState(''),
    _useState6 = _slicedToArray(_useState5, 2),
    error = _useState6[0],
    setError = _useState6[1];
  // null = not yet known. Rendering the form before the answer arrives would let someone start
  // typing a password we are about to tell them we cannot accept.
  const _useState7 = useState(null),
    _useState8 = _slicedToArray(_useState7, 2),
    entitled = _useState8[0],
    setEntitled = _useState8[1];
  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    store.admin.getUtilityCredentialEntitlementAsync.run(companyId).then(resp => {
      const data = (resp === null || resp === void 0 ? void 0 : resp.response) || resp;
      if (!cancelled) setEntitled(!!(data !== null && data !== void 0 && data.entitled));
    })
    // Fail CLOSED: if entitlement cannot be read we do not offer to take a password. The write
    // path would refuse it anyway, so offering the form would only waste the operator's typing.
    .catch(() => {
      if (!cancelled) setEntitled(false);
    });
    return () => {
      cancelled = true;
    };
  }, [store, companyId]);
  const status = credentialStatusLabel(credential);
  const failure = credentialFailureMessage(credential);

  // Takes the model as well as the call: disableUtilityCredential returns void, so the response
  // shape cannot tell success from failure - only the model's error flag can.
  const runAction = async (label, model, action) => {
    setError('');
    setBusy(label);
    try {
      const resp = await action();
      if (!succeeded(model, resp)) {
        setError("Could not ".concat(label.toLowerCase(), " this credential."));
        return;
      }
      if (onChanged) await onChanged();
    } catch (ex) {
      setError("Could not ".concat(label.toLowerCase(), " this credential."));
    } finally {
      setBusy('');
    }
  };
  const handleDisable = () => runAction('Disable', store.admin.disableUtilityCredentialAsync, () => store.admin.disableUtilityCredentialAsync.run(credential.id));
  const handleSaved = async () => {
    setIsEditing(false);
    if (onChanged) await onChanged();
  };
  return /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '16px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, "Bill automation"), /*#__PURE__*/React.createElement(ComingSoonTag, null)), error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      marginY: '8px'
    }
  }, error) : null, credential ? /*#__PURE__*/React.createElement(Stack, {
    spacing: "12px",
    sx: {
      marginY: '12px'
    }
  }, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px",
    alignItems: "center",
    flexWrap: "wrap"
  }, /*#__PURE__*/React.createElement(Chip, {
    size: "small",
    label: status,
    variant: credentialStatusVariant(credential)
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "body2"
  }, credential.usernameHint), formatDate(credential.lastVerifiedAt) ? /*#__PURE__*/React.createElement(Typography, {
    variant: "caption"
  }, "Last verified ", formatDate(credential.lastVerifiedAt)) : null), failure ? /*#__PURE__*/React.createElement(Alert, {
    severity: "warning"
  }, failure) : null, /*#__PURE__*/React.createElement(Stack, {
    direction: "row",
    spacing: "8px"
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    size: "small",
    onClick: () => setIsEditing(current => !current)
  }, isEditing ? 'Cancel' : 'Update credentials'), credential.disabledOn ? null : /*#__PURE__*/React.createElement(Button, {
    variant: "text",
    size: "small",
    disabled: !!busy,
    onClick: handleDisable
  }, "Disable"))) : /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      marginY: '8px'
    }
  }, "No credentials saved for this utility yet. Bills for it cannot be retrieved automatically."), shouldShowNotEntitledNotice(entitled, credential) ?
  /*#__PURE__*/
  /* Disabled and explained, not hidden. A form that silently vanishes is indistinguishable
     from a bug: support cannot diagnose "the field isn't there", and a customer whose
     subscription lapsed gets no signal at all. */
  React.createElement(Alert, {
    severity: "info",
    sx: {
      marginY: '8px'
    }
  }, "This company is not subscribed to Bill Automation, so portal credentials cannot be stored for it. Bills for these utilities are entered by hand.") : null, isEditing && shouldShowCredentialForm(entitled, credential) ? /*#__PURE__*/React.createElement(CredentialForm, {
    companyId: companyId,
    utilityProviderId: utilityProviderId,
    propertyId: propertyId,
    propertyUtilityId: propertyUtilityId,
    utilityTypeId: utilityTypeId,
    propertyReference: propertyReference,
    existing: credential,
    onSaved: handleSaved
  }) : null);
});